import { Component, ViewChild, ElementRef, OnInit, OnDestroy, Inject, AfterViewInit } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormControl } from '@angular/forms';
import { Router, NavigationExtras, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Consts } from '@shared/consts';
import { FeatureToggle, Regions, RoleTypeEnum } from '@shared/enums';
import { EupGlobalValidators } from '../shared/eupGlobalValidators';
import { AuthService, LoginDetails, SessionInfo } from '../services/authentication/auth.service';
import { GlobalSettingsService, GlobalSettings } from '../core/globalSettings.service';
import { Utils } from '../shared/utils.service';
import { forkJoin, Observable, Subscription, of, throwError } from 'rxjs';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { EupRoutesService } from '../core/eupRoutes.service';
import { Version } from '../../environments/app-version';
import { FeaturesToggleSettingsService } from '../featuresToggleSettings/service/featuresToggleSettings.service';
import { nonRedirectableRoutes, QueryStringParams } from '../../assets/consts';
import { LocalStorageService } from '../core/localStorage.service';
import { EupToastService } from '@core/toastr/eupToast.service';
import { PlatformDetectorService } from '@core/platformDetector.service';
import { StatusCode } from '@shared/generalInterfaces';
import { SpinnerService } from '@core/spinner/spinner.service';
import { takeUntil, tap, mergeMap, map, filter, catchError, switchMap } from 'rxjs/operators';
import { TimberService } from '@logging/timber.service';
import { BaseDestroyable } from '@core/base-destroyable';
import { DownloadNotificationService } from '../shared/downloadNotification/downloadNotification.service';
import { ShellContextService } from '../services/shell-context/shell-context.service';
import { DOCUMENT } from '@angular/common';
import { CommunicationService } from 'app/services/client-communication/communication.service';
import { AppUpdater } from '@shared/appUpdater.service';
import { FeatureToggleSettings } from 'app/featuresToggleSettings/featuresToggleSettings.model';
import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { LabService } from 'app/labs/lab.service';

@Component({
	selector: 'eup-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
})
export class LoginComponent extends BaseDestroyable implements OnInit, OnDestroy, AfterViewInit {
	loginForm: UntypedFormGroup;
	isSubmitting = false;
	isLockedUser = false;
	submitAttempted = false;
	invalidCredentials = false;
	invalidLoginMsg = null;
	showLoginTerms = true;
	showMIDCForgotPassPage = true;
	lang: string;
	termsUrl: string;
	bffVersion: string;
	appVersion: string;
	dbVersion: string;
	matForgotPasswordUrl: string;
	dbInfo: string;
	rolePath: string;
	validationState: any = {
		username: {
			valid: true,
			errors: {},
		},
		password: {
			valid: true,
			errors: {},
		},
		companies: {
			valid: true,
			errors: {},
		},
	};
	showIcpPsb = false;
	valueChangedSubscription: Subscription;
	loginSubscription: Subscription;
	loginCompanyAndDoctorSubscription: Subscription;
	loginDataSubscription: Subscription;
	ftSubscription: Subscription;
	isDoctorSetReadySubscription: Subscription;
	getAllFeaturesSubscription: Subscription;
	window: any;
	loginHint: string;
	disableEmail = false;

	@ViewChild('usernameInput') usernameInput: ElementRef;
	@ViewChild('passwordInput') passwordInput: ElementRef;
	@ViewChild('forgotPasswordButton') forgotPasswordButton: ElementRef;

	private storage: Storage;

	constructor(
		private translateService: TranslateService,
		private authService: AuthService,
		private router: Router,
		private globalSettings: GlobalSettingsService,
		private utils: Utils,
		private featuresToggleSettingsService: FeaturesToggleSettingsService,
		private eupRoutesService: EupRoutesService,
		private localStorageService: LocalStorageService,
		private eupToastService: EupToastService,
		private platformDetector: PlatformDetectorService,
		private spinnerService: SpinnerService,
		private timberService: TimberService,
		private downloadNotificationService: DownloadNotificationService,
		private shellContextService: ShellContextService,
		private activatedRoute: ActivatedRoute,
		private communincationService: CommunicationService,
		private appUpdater: AppUpdater,
		private applicationNavigationService: ApplicationNavigationService,
		private labService: LabService,
		@Inject(DOCUMENT) private document: Document
	) {
		super();
		this.storage = localStorageService.storage;
		this.utils.clearObservablesSubscriptions(this);
		this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
			this.setTermsUrl();
		});
		this.window = <any>this.document.defaultView;
	}

	ngOnDestroy(): void {
		this.utils.clearObservablesSubscriptions(this);
		this.isDoctorSetReadySubscription?.unsubscribe();
	}

	ngAfterViewInit() {
		if (this.loginHint && this.loginHint.trim()) {
		  this.passwordInput.nativeElement.focus();
		}
	}

	ngOnInit(): void {
		const adaChatBotVisible = document.querySelector('#ada-entry');
		if (!!adaChatBotVisible) {
			if (!!this.window.adaEmbed) {
				this.window.adaEmbed.reset();
				this.window.adaEmbed.stop();
			}
		}
		this.deleteUserDataFromStorage();
		this.activatedRoute.queryParams.subscribe((queryParams) => {
			if (queryParams.callback && queryParams.callback === '0') {
				this.eupToastService.error('', this.translateService.instant('Login.AzureAdErrorMessage'), true, {}, true);
			}
		});
		this.activatedRoute.queryParams.subscribe(queryParams => {
			this.loginHint = queryParams['login_hint'];
			this.loginForm = new UntypedFormGroup({
				username: new UntypedFormControl(!this.loginHint?.trim() ? '' : this.loginHint, [Validators.required, EupGlobalValidators.email]),
				password: new UntypedFormControl('', [Validators.required]),
			});


			const featureFlags = [FeatureToggle.IMSUserFlow, FeatureToggle.Auth0IsEnabled];

			this.featuresToggleSettingsService.getFeaturesToggleSettings()
				.pipe(
					takeUntil(this.componentAlive$),
					map(async (res: FeatureToggleSettings[]) => {
						this.disableEmail = (this.loginHint && this.loginHint.trim()) && featureFlags.every((f) => res.some((ff) => ff.id === f && ff.isActive));
					})).subscribe();
		});
	
		this.getLoginData();
		this.eupRoutesService.TermsUrlObs.pipe(
			takeUntil(this.componentAlive$),
			tap(() => {
				this.setFeaturesTogglesVisibility();
				this.setTermsUrl();
			})
		).subscribe();
	
		this.valueChangedSubscription = this.loginForm.valueChanges.subscribe((data: any) => this.onValueChanged(data));
		this.onValueChanged(); // (re)set validation messages now
		const ieTimer = setTimeout(() => {
			if (this.platformDetector.isIE11OrOlder) {
				this.eupToastService.error(null, this.translateService.instant('Errors.Warning_IE'), true, null, true);
			}
			clearTimeout(ieTimer);
		});
	}

	deleteUserDataFromStorage() {
		this.shellContextService.resetContext();
		this.applicationNavigationService.reset();
		this.labService.reset();
		this.storage.removeItem(Consts.Storage.Settings);
		this.storage.removeItem(Consts.Storage.SessionInfo);
	}
	setFeaturesTogglesVisibility(): void {
		this.ftSubscription = forkJoin([
			this.featuresToggleSettingsService.isVisibleFeature$(FeatureToggle.LoginTerms),
			this.featuresToggleSettingsService.isVisibleFeature$(FeatureToggle.ForgotPasswordInMIDC),
		])
			.pipe(
				tap(([isLoginTermsVisible, isMIDCPageForgotPassVisible]) => {
					this.showLoginTerms = isLoginTermsVisible;
					this.showMIDCForgotPassPage = isMIDCPageForgotPassVisible;
				})
			)
			.subscribe();
	}

	setTermsUrl(): void {
		this.termsUrl = this.eupRoutesService.TermsUrl
			? this.eupRoutesService.TermsUrl.split('{0}').join(this.translateService.currentLang)
			: '';
	}

	getLoginData(): void {
		this.loginDataSubscription = this.authService.getDetails().subscribe((res: LoginDetails) => {
			this.showIcpPsb = res.region === Regions.china;
			this.bffVersion = res.appVersion;
			this.dbVersion = res.databaseVersion;
			this.matForgotPasswordUrl = this.showMIDCForgotPassPage
				? '/recoverpassword'
				: res.forgotPasswordUrl.replace('{0}', this.translateService.currentLang);
			this.dbInfo = res.dbInfo;
			this.appVersion = Version.version;
			this.storage.setItem(Consts.Storage.ServerVersion, res.appVersion);
		});
	}

	forwardToPage(): void {
		const params = this.router.parseUrl(this.router.url).queryParams;
		const navExtras: NavigationExtras = { queryParams: {} };
		if (!!params['companyid']) {
			const urlParamIsValid = this.checkUrlForCompany(0) !== 0;
			if (urlParamIsValid) {
				navExtras.queryParams['companyid'] = params['companyid'];
			}
		}

		let returnUrl = !!params['returnUrl'] ? params['returnUrl'] : params['returnurl'];

		let navigationUrl;

		if (!!returnUrl && this.isValidRedirectUrl(returnUrl)) {
			if (returnUrl.indexOf('/dist/') >= 0) {
				returnUrl = returnUrl.replace('/dist/', '/'); // fix for local redirection
			}

			const returnUrlParams = this.router.parseUrl(returnUrl).queryParams;
			Object.assign(navExtras.queryParams, returnUrlParams);

			navigationUrl = returnUrl.split('?')[0];

			this.router.navigate([navigationUrl], navExtras);
		} else {
			navigationUrl = `/${this.rolePath}/home`;
			this.router.navigate([navigationUrl], navExtras);
		}
		//wait for navagation to end
		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => { 
			this.appUpdater.checkForUpdates();
		});
	}

	submitLogin(user: any) {
		this.deleteUserDataFromStorage();
		this.submitAttempted = true;
		this.invalidCredentials = false;
		this.invalidLoginMsg = null;
		this.isLockedUser = false;

		this.onValueChanged();
		if (!this.loginForm.valid) {
			// focus on username field
			this.usernameInput.nativeElement.focus();
			return;
		}

		this.isSubmitting = true;

		try {
			const mail = this.utils.hideEmail(this.loginForm?.get('username')?.value);
			this.timberService.info('App version is:' + Version.version + ' while trying login to user: ' + mail, {
				module: 'LoginComponent',
			});
		} catch {}

		this.loginSubscription = this.loginUser().subscribe({
			next: () => {
				this.authService.authStatus$.next({ isAuthenticated: true, userId: this.globalSettings.get().contactId });
			},
			error: (err) => {
				this.isSubmitting = false;
				switch (err.status) {
					case StatusCode.Unauthorized:
					case StatusCode.Forbidden:
						this.usernameInput.nativeElement.focus();
						this.invalidCredentials = true;
						break;
					case StatusCode.Locked:
						this.isLockedUser = true;
						break;
				}
			}
		});
	}

	loginWithSession(enableLoginFallback: boolean): Observable<GlobalSettings> {
		return this.authService.startSession(this.loginForm.value).pipe(
			mergeMap((res) => {
				if (res.httpStatusCode === StatusCode.Redirect)
					return of({ shouldRedirect: true, redirectUrl: res.redirectUrl } as GlobalSettings);
				return this.authService.proceedLoginFlow(
					{ accessToken: res.accessToken, sessionId: res.sessionId, idToken: res.idToken  } as SessionInfo,
					this.loginForm.value
				);
			}),
			catchError((err) => {
				try {
					const user = this.utils.hideEmail(this.loginForm?.get('username')?.value);
					this.timberService.error(`Error in new login process for user ${user}`, { module: 'LoginComponent', error: err });
				} catch (e: any) {
					console.log(e);
				}
				localStorage.removeItem(Consts.Storage.SessionInfo);
				localStorage.removeItem(Consts.Storage.Settings);
				if (enableLoginFallback) return this.loginWithoutSession();
				return throwError(err);
			})
		);
	}

	loginWithoutSession(): Observable<GlobalSettings> {
		return this.authService.login(this.loginForm.value).pipe(
			mergeMap((res) => {
				if (res.httpStatusCode === StatusCode.Redirect)
					return of({ shouldRedirect: true, redirectUrl: res.redirectUrl } as GlobalSettings);
				return of(res as GlobalSettings);
			})
		);
	}

	loginUser(): Observable<void> {
		return this.featuresToggleSettingsService.getFeaturesToggleSettings().pipe(
			mergeMap((res) => {
				const sctOn = res.find((f) => f.id === FeatureToggle.UseSctAuthorization)?.isActive === true;
				const enableLoginFallback = res.find((f) => f.id === FeatureToggle.EnableLoginFallback)?.isActive === true;
				if (sctOn) return this.loginWithSession(enableLoginFallback);
				return this.loginWithoutSession();
			}),
			tap((res) => {
				if (res.shouldRedirect) {
					window.location.href = res.redirectUrl;
					return of({ redirect: true });
				}
			}),
			filter((redirect) => !!redirect),
			map((res) => {
				this.setUserData(res, new Date().getTime());
				this.timberService.sessionStartEvent(res.contactId, res.companies[0].id, res.roleType);
			})
		);
	}

	setUserData(settings: GlobalSettings, loginStartingTime: number) {
		if (settings.roleType === RoleTypeEnum.Internal) {
			this.globalSettings.initialize({ ...settings, roleType: RoleTypeEnum.Internal });
			return;
		}
		settings.loginEmail = this.loginForm.value ? this.loginForm.value.username : '';

		if ((settings as any).errorReasonText) {
			this.invalidLoginMsg = (settings as any).errorReasonText;
			this.isSubmitting = false;
			return;
		}

		if (settings.companies.length === 0) {
			this.validationState.companies.valid = false;
			this.validationState.companies.errors['noCompany'] = true;
			this.isSubmitting = false;
			return;
		}

		this.downloadNotificationService.initialize();
		this.globalSettings.initialize(settings);
		this.rolePath = this.globalSettings.rolePath();
		this.isSubmitting = false;

		let companyId = settings.companies[0].id;
		companyId = this.checkUrlForCompany(companyId);

		this.spinnerService.start();
		this.loginCompanyAndDoctorSubscription = this.globalSettings
			.setCompanyAndDefaultDoctor(companyId)
			.pipe(
				tap(
					(data: any) => {
						const timeElapsed = new Date().getTime() - loginStartingTime;
						let region = settings.companies.find((c) => c.id === companyId).region;
						this.communincationService.setRegion(region);
						this.shellContextService.updateContext((ctx) => {
							ctx.bizCtx.region = region;
						});
						this.isDoctorSetReadySubscription = this.globalSettings.isDoctorSetReady$
							.pipe(
								tap((isDoctorSetReady) => {
									if (isDoctorSetReady) {
										if (settings.roleType === RoleTypeEnum.Lab) {
											this.timberService.info(
												`Submit Login: Elapsed time from submit click to page navigation: ${
													timeElapsed / 1000
												} seconds. Logged in user ${settings.contactId} - Company Id ${
													settings.selectedCompanyId
												} - Role ${settings.roleType}`,
												{ module: 'LoginComponent' }
											);
										}
										this.forwardToPage();
										this.spinnerService.stop();
									}
								}),
								takeUntil(this.componentAlive$)
							)
							.subscribe();
					},
					() => this.spinnerService.stop()
				),
				takeUntil(this.componentAlive$)
			)
			.subscribe();		
	}

	onValueChanged(data?: any) {
		if (!this.loginForm || !this.submitAttempted) {
			return;
		}
		const form = this.loginForm;
		const fieldNames = Object.keys(this.validationState);
		for (let fieldNameIdx = 0; fieldNameIdx < fieldNames.length; fieldNameIdx++) {
			const fieldName = fieldNames[fieldNameIdx];
			const field: any = (<any>this.validationState)[fieldName];
			const control = form.get(fieldName);

			// reset all message
			field.valid = true;
			field.errors = {};

			if (control && (!control.valid || this.invalidCredentials || this.invalidLoginMsg)) {
				field.valid = false;

				for (const key in control.errors) {
					field.errors[key] = true;

					// break to show only one error per control at a time
					break;
				}
			}
		}
	}

	openRecoverPassword(): void {
		window.open(`${this.matForgotPasswordUrl}?${QueryStringParams.LANGUAGE}=${this.translateService.currentLang}`, '_blank');
	}

	private checkUrlForCompany(id: number): number {
		const params = this.router.parseUrl(this.router.url.toLowerCase()).queryParams;
		let companyIdfromParams = 0;

		if (!!params['companyid']) {
			companyIdfromParams = +params['companyid'];
		} else if (!!params['returnurl']) {
			const returnUrlparams = this.router.parseUrl(params['returnurl']).queryParams;
			if (!!returnUrlparams['companyid']) {
				companyIdfromParams = +returnUrlparams['companyid'];
			}
		}
		const validCompanyIdUsed = this.globalSettings.get().companies.filter((x) => x.id === companyIdfromParams).length === 1;
		return validCompanyIdUsed ? companyIdfromParams : id;
	}

	private isValidRedirectUrl(url: string): boolean {
		url = url.toLowerCase();
		const subRoutes = url.split('/');
		const lastSubRoute = subRoutes[subRoutes.length - 1];
		return !nonRedirectableRoutes.some((r) => lastSubRoute.indexOf(r) >= 0);
	}
}
